import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { profilesApi, api as axiosApi } from "../../api";
import { chatsApi, ChatType } from "../../api/chats";
import { presenceApi } from "../../api/presence";
import { UserContext } from "../../context/UserContext";
import { subscribeToUserPresence } from "../../api/websocket";
import "../../css/components/UserProfileCard.css";
import HrnbCard from "../controls/HrnbCard";
import HrnbImage from "../controls/HrnbImage";
import { HrnbListGroup, HrnbListGroupItem } from "../controls/HrnbListGroup";
import HrnbLabel from "../controls/HrnbLabel";
import HrnbLoader from "../controls/HrnbLoader";
import HrnbButton from "../controls/HrnbButton";
import HrnbAlert from "../controls/HrnbAlert";
import OnlineStatusIndicator from "../controls/OnlineStatusIndicator";
import { SendMessageIcon } from "../../icons";
import { DefaultAvatar } from "../../images";
import MessagePopup from "./../messages/MessagePopup";

export default function UserProfileCard({ profile, onSendMessage }) {
    const { currentUser } = useContext(UserContext);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMessagePopupOpen, setIsMessagePopupOpen] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [lastActivity, setLastActivity] = useState(null);
    const [sending, setSending] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!profile) {
            setAvatarUrl(null);
            setLoading(false);
            return;
        }

        if (!profile.avatar) {
            setAvatarUrl(null);
            setLoading(false);
            return;
        }

        let urlObject = null;
        let cancelled = false;

        const fetchAvatar = async () => {
            setLoading(true);
            setError(null);
            try {
                // Нормализуем avatar: может быть UUID или путём /api/files/{uuid}
                const rawAvatar = profile.avatar;
                const normalizedAvatar = typeof rawAvatar === 'string' && rawAvatar.startsWith('/api/files/')
                    ? rawAvatar.replace('/api/files/', '')
                    : rawAvatar;

                if (profilesApi && typeof profilesApi.getAvatar === "function") {
                    const blob = await profilesApi.getAvatar(normalizedAvatar);
                    urlObject = URL.createObjectURL(blob);
                } else {
                    const resp = await axiosApi.get(`/profiles/avatar/${normalizedAvatar}`, {responseType: "blob"});
                    urlObject = URL.createObjectURL(resp.data);
                }
                if (!cancelled) setAvatarUrl(urlObject);
            } catch (err) {
                if (!cancelled) {
                    console.warn("Avatar load error:", err);
                    setError("Не удалось загрузить аватар");
                    setAvatarUrl(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchAvatar();

        return () => {
            cancelled = true;
            if (urlObject) URL.revokeObjectURL(urlObject);
        };
    }, [profile]);

    // Загрузка онлайн-статуса и подписка на обновления
    useEffect(() => {
        if (!profile?.userId) return;

        presenceApi.getStatus(profile.userId)
            .then(data => {
                setIsOnline(data.isOnline);
                setLastActivity(data.lastActivity);
            })
            .catch(err => console.error('[UserProfileCard] Presence error:', err));

        const subscription = subscribeToUserPresence(profile.userId, (presence) => {
            setIsOnline(presence.isOnline);
            setLastActivity(presence.lastActivity);
        });

        return () => {
            if (subscription) subscription.unsubscribe();
        };
    }, [profile?.userId]);

    const handleGoToProfile = () => {
        if (!profile?.id) return;
        navigate(`/profile/${profile.id}`);
    };

    const handleOpenMessagePopup = (e) => {
        e?.stopPropagation();
        setIsMessagePopupOpen(true);
    };

    const handleCloseMessagePopup = () => {
        setIsMessagePopupOpen(false);
    };

    const handleSendMessage = async ({ profile, message }) => {
        if (typeof onSendMessage === "function") {
            await onSendMessage({ profile, message });
            setIsMessagePopupOpen(false);
            return;
        }

        const recipientId = profile?.id;
        const currentProfileId = currentUser?.profiles?.[0]?.id;

        if (!recipientId) {
            setError("Не удалось определить получателя");
            return;
        }

        setSending(true);
        setError(null);
        try {
            // 1. Найти существующий DIRECT-чат с получателем
            let chatId = null;
            try {
                const chats = await chatsApi.listChats({ chatType: ChatType.DIRECT });
                const existing = chats.find(
                    c => c.chatType === ChatType.DIRECT &&
                        (c.participantIds || c.participants || []).includes(recipientId)
                );
                if (existing) chatId = existing.id;
            } catch (err) {
                console.warn("[UserProfileCard] Не удалось найти существующий чат:", err.message);
            }

            // 2. Если чата нет — создать DIRECT-чат
            if (!chatId) {
                if (!currentProfileId) {
                    throw new Error("Не удалось определить ваш профиль");
                }
                const newChat = await chatsApi.createChat({
                    chatType: ChatType.DIRECT,
                    participants: [currentProfileId, recipientId]
                });
                chatId = newChat.id;
            }

            // 3. Отправить сообщение через POST /api/messages/send
            await chatsApi.sendMessageAlt({
                chatId,
                senderId: currentProfileId,
                content: message
            });

            alert("Сообщение отправлено!");
            setIsMessagePopupOpen(false);
        } catch (err) {
            console.error("Ошибка отправки сообщения:", err);
            setError("Ошибка отправки сообщения");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {error && <HrnbAlert type="danger">{error}</HrnbAlert>}

            <div style={{cursor: "pointer"}} onClick={handleGoToProfile}>
                <HrnbCard title={profile?.displayName || "Профиль"}>
                    {loading ? (
                        <HrnbLoader size={48} color="#2399E3" text="HRNB"/>
                    ) : (
                        <div className="online-status-avatar">
                            <HrnbImage src={avatarUrl || DefaultAvatar} rounded size={150}/>
                            {profile?.userId && (
                                <OnlineStatusIndicator
                                    isOnline={isOnline}
                                    lastActivity={lastActivity}
                                    size="lg"
                                    showLabel={false}
                                />
                            )}
                        </div>
                    )}

                    <HrnbListGroup>
                        <HrnbListGroupItem>
                            <HrnbLabel text="Роль:"/>
                            <HrnbLabel text={profile?.profileType || "-"}/>
                        </HrnbListGroupItem>

                        <HrnbListGroupItem>
                            <HrnbLabel text="Описание:"/>
                            <div style={{marginTop: 6}}>{profile?.bio || "-"}</div>
                        </HrnbListGroupItem>
                    </HrnbListGroup>

                    <div style={{marginTop: 20}}>
                        <HrnbButton
                            variant="primary"
                            icon={<SendMessageIcon/>}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMessagePopup(e);
                            }}
                        >
                            Отправить сообщение
                        </HrnbButton>
                    </div>
                </HrnbCard>
            </div>

            <MessagePopup
                isOpen={isMessagePopupOpen}
                onClose={handleCloseMessagePopup}
                profile={profile}
                onSendMessage={handleSendMessage}
                sending={sending}
            />
        </>
    );
}
